import { gql } from '@apollo/client';
import IconSatellite3 from '@apollo/icons/default/IconSatellite3.svg';
import classnames from 'classnames';
import React, { useMemo } from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { CheckWorkflowStatusIcon } from './CheckWorkflowStatusIcon';

export const DownstreamWorkflowStatusCountsFragment = gql`
  fragment DownstreamWorkflowStatusCountsFragment on DownstreamCheckTask {
    id
    results {
      failsUpstreamWorkflow
      downstreamWorkflow {
        id
        status
      }
    }
  }
`;

interface Props {
  downstreamTask: GraphQLTypes.DownstreamWorkflowStatusCountsFragment;
  className?: string;
  includeIcon?: boolean;
}

export const DownstreamWorkflowStatusCounts = ({
  downstreamTask,
  className,
  includeIcon = true,
}: Props) => {
  const { failedCount, warningCount, passedCount, pendingCount } = useMemo(
    () => getDownstreamWorkflowStatusCounts(downstreamTask),
    [downstreamTask],
  );

  if (failedCount + warningCount + passedCount + pendingCount === 0)
    return null;

  return (
    <div
      className={classnames('flex flex-nowrap items-center gap-2 ', className)}
    >
      {includeIcon && <IconSatellite3 />}
      {failedCount > 0 ? (
        <WorkflowStatusCount status="FAILED" count={failedCount} />
      ) : null}
      {warningCount > 0 ? (
        <WorkflowStatusCount status="WARNING" count={warningCount} />
      ) : null}
      {passedCount > 0 ? (
        <WorkflowStatusCount status="PASSED" count={passedCount} />
      ) : null}
      {pendingCount > 0 ? (
        <WorkflowStatusCount status="PENDING" count={pendingCount} />
      ) : null}
    </div>
  );
};

const getDownstreamWorkflowStatusCounts = (
  downstreamTask: GraphQLTypes.DownstreamWorkflowStatusCountsFragment,
) => {
  const statusCounts = {
    passedCount: 0,
    failedCount: 0,
    warningCount: 0,
    pendingCount: 0,
  };

  downstreamTask?.results?.forEach(
    ({ failsUpstreamWorkflow, downstreamWorkflow }) => {
      // If the downstream workflow doesn't exist, it technically has status
      // PENDING, but we don't show a line item for in lists of downstream
      // workflows since we don't have much information on it. So to avoid the
      // UI showing inconsistent counts of downstream workflows, we just don't
      // count it at all when it doesn't exist.
      if (!downstreamWorkflow?.status) return;
      switch (downstreamWorkflow.status) {
        case GraphQLTypes.CheckWorkflowStatus.FAILED: {
          if (failsUpstreamWorkflow) {
            statusCounts.failedCount += 1;
          } else {
            statusCounts.warningCount += 1;
          }
          break;
        }
        case GraphQLTypes.CheckWorkflowStatus.PASSED: {
          statusCounts.passedCount += 1;
          break;
        }
        case GraphQLTypes.CheckWorkflowStatus.PENDING: {
          statusCounts.pendingCount += 1;
          break;
        }
        default:
          return assertUnreachable(downstreamWorkflow.status);
      }
    },
  );

  return statusCounts;
};

const WorkflowStatusCount = ({
  status,
  count,
}: {
  status: 'PASSED' | 'FAILED' | 'PENDING' | 'WARNING';
  count: number;
}) => (
  <div className="flex items-center">
    <CheckWorkflowStatusIcon className="mr-2" status={status} />
    {count}
  </div>
);
